"use client";

import * as React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Search,
  X,
  Filter,
  ChevronDown,
  ChevronUp,
  Tag,
  Briefcase,
  Share2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Card, CardContent } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";

interface SearchFilters {
  name?: string;
  tags?: string[];
  projectId?: string;
  isSharable?: boolean;
}

interface AdvancedSearchProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  availableTags: string[];
  availableProjects: Array<{ id: string; name: string }>;
  placeholder?: string;
  className?: string;
  showClearAll?: boolean;
}

export const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  filters,
  onFiltersChange,
  availableTags,
  availableProjects,
  placeholder = "Search by name...",
  className,
  showClearAll = true,
}) => {
  const [searchValue, setSearchValue] = React.useState(filters.name || "");
  const [isOpen, setIsOpen] = React.useState(false);
  const [tagPopoverOpen, setTagPopoverOpen] = React.useState(false);
  const { t } = useTranslation("common");

  // Sync searchValue with filters.name
  React.useEffect(() => {
    setSearchValue(filters.name || "");
  }, [filters.name]);

  // Check if any advanced filters are active
  const hasAdvancedFilters = !!(
    filters.tags?.length ||
    filters.projectId ||
    filters.isSharable !== undefined
  );

  // Auto-expand advanced section if advanced filters are active
  React.useEffect(() => {
    if (hasAdvancedFilters) {
      setIsOpen(true);
    }
  }, [hasAdvancedFilters]);

  const handleNameChange = (value: string) => {
    setSearchValue(value);
    onFiltersChange({
      ...filters,
      name: value || undefined,
    });
  };

  const addTag = (tag: string) => {
    const currentTags = filters.tags || [];
    if (!currentTags.includes(tag)) {
      onFiltersChange({
        ...filters,
        tags: [...currentTags, tag],
      });
    }
  };

  const removeTag = (tagToRemove: string) => {
    const currentTags = filters.tags || [];
    onFiltersChange({
      ...filters,
      tags: currentTags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleProjectChange = (projectId: string) => {
    onFiltersChange({
      ...filters,
      projectId: projectId === "all" ? undefined : projectId,
    });
  };

  const handleSharableChange = (checked: boolean | "indeterminate") => {
    onFiltersChange({
      ...filters,
      isSharable: checked === "indeterminate" ? undefined : checked,
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({});
    setSearchValue("");
  };

  const hasActiveFilters = !!(
    filters.name ||
    filters.tags?.length ||
    filters.projectId ||
    filters.isSharable !== undefined
  );

  return (
    <Card className={cn("border-0 shadow-sm p-0", className)}>
      <CardContent className="p-0 space-y-4">
        <Collapsible
          open={isOpen}
          onOpenChange={setIsOpen}
          className="w-full space-y-4"
        >
          {/* Main Search Input and Advanced Filters Toggle - Same Row */}
          <div className="flex items-center gap-3">
            {/* Main Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                type="text"
                placeholder={placeholder}
                value={searchValue}
                onChange={(e) => handleNameChange(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Advanced Filters Toggle */}
            <CollapsibleTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-10 px-3 text-sm flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {t("previewsList.filter.advanced")}
                </span>
                {isOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
            </CollapsibleTrigger>

            {/* Clear All Button */}
            {hasActiveFilters && showClearAll && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-10 px-3 text-sm text-muted-foreground hover:text-foreground flex-shrink-0"
              >
                {t("previewsList.filter.clearAll")}
              </Button>
            )}
          </div>

          {/* Active Filters Summary */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 items-center pt-2">
              {filters.tags &&
                filters.tags.length > 0 &&
                filters.tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="text-xs flex items-center gap-1 px-2 py-1"
                  >
                    {tag}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 hover:bg-transparent"
                      onClick={() => removeTag(tag)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              {filters.projectId && (
                <Badge
                  variant="secondary"
                  className="text-xs flex items-center gap-1 px-2 py-1"
                >
                  Project:{" "}
                  {
                    availableProjects.find((p) => p.id === filters.projectId)
                      ?.name
                  }
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 hover:bg-transparent"
                    onClick={() => handleProjectChange("all")}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {filters.isSharable !== undefined && (
                <Badge
                  variant="secondary"
                  className="text-xs flex items-center gap-1 px-2 py-1"
                >
                  {filters.isSharable ? "Sharable" : "Private"}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 hover:bg-transparent"
                    onClick={() => handleSharableChange("indeterminate")}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
            </div>
          )}

          {/* Advanced Filters Section */}
          <CollapsibleContent className="space-y-6 pt-2">
            <div className="grid gap-6 md:grid-cols-3">
              {/* Tags Filter */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <label className="text-sm font-medium">
                    {t("previewsList.filter.tags.title")}
                  </label>
                </div>
                <Popover open={tagPopoverOpen} onOpenChange={setTagPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between text-sm"
                    >
                      {t("previewsList.filter.tags.select")}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder={t("previewsList.filter.tags.search")}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {t("previewsList.filter.tags.noFound")}
                        </CommandEmpty>
                        <CommandGroup>
                          {availableTags
                            .filter((tag) => !filters.tags?.includes(tag))
                            .map((tag) => (
                              <CommandItem
                                key={tag}
                                value={tag}
                                onSelect={() => {
                                  addTag(tag);
                                  setTagPopoverOpen(false);
                                }}
                              >
                                {tag}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Project Filter */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  <label className="text-sm font-medium">
                    {t("previewsList.filter.projects.title")}
                  </label>
                </div>
                <Select
                  value={filters.projectId || "all"}
                  onValueChange={handleProjectChange}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue
                      placeholder={t("previewsList.filter.projects.all")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("previewsList.filter.projects.all")}
                    </SelectItem>
                    {availableProjects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sharable Filter */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Share2 className="h-4 w-4 text-muted-foreground" />
                  <label className="text-sm font-medium">
                    {t("previewsList.filter.shareStatus.title")}
                  </label>
                </div>
                <div className="space-y-2 bg-muted/30 p-3 rounded-md">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sharable-any"
                      checked={filters.isSharable === undefined}
                      onCheckedChange={() =>
                        handleSharableChange("indeterminate")
                      }
                    />
                    <label htmlFor="sharable-any" className="text-sm">
                      {t("previewsList.filter.shareStatus.any")}
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sharable-true"
                      checked={filters.isSharable === true}
                      onCheckedChange={() => handleSharableChange(true)}
                    />
                    <label htmlFor="sharable-true" className="text-sm">
                      {t("previewsList.filter.shareStatus.sharable")}
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sharable-false"
                      checked={filters.isSharable === false}
                      onCheckedChange={() => handleSharableChange(false)}
                    />
                    <label htmlFor="sharable-false" className="text-sm">
                      {t("previewsList.filter.shareStatus.private")}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

export default AdvancedSearch;
